import type { ProductCardResponse } from "@/shared/api/generated/iSHONCHPlatformAPI.schemas";

/**
 * Favorites of a visitor who is not logged in, kept in this browser (the Избранное tab works for everyone, D10).
 * Each entry is the product card as the visitor saw it — catalog data, nothing personal — so the tab can show the
 * cards without a request. Prices on the cards are refreshed from the server after login, when the ids move to
 * the account and this store is cleared.
 */
const KEY = "ishonch.favorites.v1";
const PRODUCT_ID = /^prd_[0-9a-f]{32}$/;
export const MAX_GUEST_FAVORITES = 100;

let items: readonly ProductCardResponse[] = read();
const listeners = new Set<() => void>();

function isCard(value: unknown): value is ProductCardResponse {
  if (typeof value !== "object" || value === null) return false;
  const card = value as Partial<ProductCardResponse>;
  return (
    typeof card.p_id === "string" &&
    PRODUCT_ID.test(card.p_id) &&
    typeof card.p_slug === "string" &&
    typeof card.p_name === "string" &&
    typeof card.p_price === "number" &&
    typeof card.p_monthly_payment === "number" &&
    typeof card.p_installment_months === "number"
  );
}

function read(): ProductCardResponse[] {
  try {
    const raw = localStorage.getItem(KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter(isCard).slice(0, MAX_GUEST_FAVORITES) : [];
  } catch {
    return [];
  }
}

function write(next: readonly ProductCardResponse[]) {
  items = next;
  try {
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    // ignore: favorites just won't survive a reload
  }
  listeners.forEach((listener) => {
    listener();
  });
}

export const guestFavorites = {
  getItems: (): readonly ProductCardResponse[] => items,

  subscribe: (listener: () => void): (() => void) => {
    listeners.add(listener);
    const onStorage = (event: StorageEvent) => {
      if (event.key === KEY) {
        items = read();
        listener();
      }
    };
    window.addEventListener("storage", onStorage);
    return () => {
      listeners.delete(listener);
      window.removeEventListener("storage", onStorage);
    };
  },

  /** Adds the card at the top, or removes it when it is already there. */
  toggle(card: ProductCardResponse) {
    if (items.some((item) => item.p_id === card.p_id)) {
      write(items.filter((item) => item.p_id !== card.p_id));
    } else {
      write([card, ...items].slice(0, MAX_GUEST_FAVORITES));
    }
  },

  clear() {
    write([]);
  },

  /** Forgets these products (moved into the account, or gone from the catalog). */
  removeMany(ids: readonly string[]) {
    write(items.filter((item) => !ids.includes(item.p_id)));
  },
};
